import React from 'react';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Code, Users, Download, ExternalLink, Star, GitBranch, Zap } from "lucide-react";

const Resources: React.FC = () => {
  const tools = [
    {
      name: "AI Code Assistant",
      description: "Intelligent code completion and generation powered by advanced AI models",
      icon: <Zap className="w-6 h-6" />,
      category: "AI Tools",
      rating: 4.9,
      downloads: "50K+",
      link: "#"
    },
    {
      name: "React DevTools Pro",
      description: "Enhanced debugging and performance monitoring for React applications",
      icon: <Code className="w-6 h-6" />,
      category: "Development",
      rating: 4.8,
      downloads: "120K+",
      link: "#"
    },
    {
      name: "TypeScript Boilerplate",
      description: "Production-ready TypeScript project templates with best practices",
      icon: <GitBranch className="w-6 h-6" />,
      category: "Templates",
      rating: 4.7,
      downloads: "75K+",
      link: "#"
    }
  ];

  const tutorials = [
    {
      title: "Mastering React Hooks",
      description: "Complete guide to React Hooks with practical examples and advanced patterns",
      duration: "2h 30m",
      level: "Intermediate",
      students: "15,420",
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop"
    },
    {
      title: "TypeScript Best Practices",
      description: "Learn advanced TypeScript techniques for scalable application development",
      duration: "3h 15m",
      level: "Advanced",
      students: "8,930",
      image: "https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=400&h=250&fit=crop"
    },
    {
      title: "AI-Powered Development",
      description: "Integrate AI tools into your development workflow for maximum productivity",
      duration: "1h 45m",
      level: "Beginner",
      students: "22,150",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop"
    }
  ];

  const communityFeatures = [
    {
      title: "Code Reviews",
      description: "Get expert feedback on your code from experienced developers",
      icon: <BookOpen className="w-8 h-8" />,
      members: "5,200+"
    },
    {
      title: "Live Coding Sessions",
      description: "Join interactive coding sessions and learn from industry professionals",
      icon: <Users className="w-8 h-8" />,
      members: "3,800+"
    },
    {
      title: "Project Collaboration",
      description: "Find teammates for open-source projects and hackathons",
      icon: <GitBranch className="w-8 h-8" />,
      members: "7,100+"
    }
  ];

  return (
    <section id="resources" className="py-20 bg-gradient-to-br from-slate-50 to-purple-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-cyan-500 to-pink-500 bg-clip-text text-transparent mb-6">
            Developer Resources
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover powerful tools, comprehensive tutorials, and vibrant community features designed to accelerate your coding journey
          </p>
        </div>

        {/* Coding Tools Section */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <Code className="w-8 h-8 text-purple-600" />
            <h3 className="text-3xl font-bold text-gray-800">Coding Tools</h3>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {tools.map((tool, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white">
                        {tool.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <h4 className="text-xl font-bold text-gray-800">{tool.name}</h4>
                          <span className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded-full w-fit">
                            {tool.category}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{tool.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span>{tool.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Download className="w-4 h-4" />
                            <span>{tool.downloads} downloads</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-6 py-2 w-fit">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Try Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tutorials Section */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <BookOpen className="w-8 h-8 text-cyan-600" />
            <h3 className="text-3xl font-bold text-gray-800">Featured Tutorials</h3>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {tutorials.map((tutorial, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-80 h-48 lg:h-auto">
                      <img 
                        src={tutorial.image} 
                        alt={tutorial.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 p-8">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                        <div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">{tutorial.title}</h4>
                          <p className="text-gray-600 mb-4">{tutorial.description}</p>
                        </div>
                        <span className={`text-sm px-3 py-1 rounded-full w-fit ${
                          tutorial.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                          tutorial.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {tutorial.level}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                          <span>⏱️ {tutorial.duration}</span>
                          <span>👥 {tutorial.students} students</span>
                        </div>
                        <Button className="bg-gradient-to-r from-cyan-600 to-pink-600 hover:from-cyan-700 hover:to-pink-700 text-white">
                          Start Learning
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Community Features Section */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <Users className="w-8 h-8 text-pink-600" />
            <h3 className="text-3xl font-bold text-gray-800">Community Features</h3>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {communityFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex items-center gap-6 flex-1">
                      <div className="p-4 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white">
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h4>
                        <p className="text-gray-600 mb-3">{feature.description}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>{feature.members} active members</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" className="border-2 border-pink-200 hover:bg-pink-50 text-pink-700 px-6 py-2 w-fit">
                      Join Community
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Card className="max-w-2xl mx-auto border-0 bg-gradient-to-r from-purple-600 via-cyan-600 to-pink-600 text-white">
            <CardContent className="p-8">
              <h4 className="text-2xl font-bold mb-4">Ready to Level Up Your Coding Skills?</h4>
              <p className="text-purple-100 mb-6">
                Join thousands of developers who are already using our resources to build amazing projects
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-3">
                  Get Started Free
                </Button>
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-3">
                  Explore Resources
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Resources;